import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Image,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PIM_SQUARE_THUMBNAIL_PX, resolveProductThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';

/**
 * Inline content for a single radio option: thumbnail + name lines + radio indicator.
 * Thumbnail, extra price, View details: `ConfigurationSelector` option-step theme parts.
 * Optionally shows "View details" link on mobile when onViewDetails is provided (multi-slot context).
 * Optionally shows "+ $X" extra price below name when extraPrice > 0.
 */
function trimNameLine(value) {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : String(value);

  return s.trim();
}

function RadioOptionNameBlock({
  containerId,
  cushionSingleTitle,
  displayPrimary,
  isOutOfStock,
  line2Trimmed,
  onViewDetails,
  product,
  shortDescription,
  showSecondaryNameLine,
  styles,
  useCushionSingleTitle,
  useListLayout,
  handleViewDetails,
}) {
  return (
    <Flex
      className="radio-option-content-name-container"
      flex={1}
      flexDirection="column"
      minWidth={0}
      gap="8px"
      maxWidth={useListLayout ? undefined : {base: '142px', lg: '246px'}}
    >
      {useCushionSingleTitle ? (
        <Text className="radio-option-content-name2" {...styles?.nameLine2}>
          {cushionSingleTitle}
        </Text>
      ) : (
        <>
          <Text className="radio-option-content-name1" {...styles?.nameLine1}>
            {displayPrimary}
          </Text>
          {showSecondaryNameLine && (
            <Text className="radio-option-content-name2" {...styles?.nameLine2}>
              {line2Trimmed}
            </Text>
          )}
        </>
      )}
      {shortDescription && (
        <Text className="radio-option-content-description" {...styles?.radioOptionDescription}>
          {shortDescription}
        </Text>
      )}
      {isOutOfStock && (
        <Text className="radio-option-content-oos" color="red.500" fontSize="xs">
          <FormattedMessage
            defaultMessage="Sold out"
            id="customize_panel.option_step_radio.sold_out"
          />
        </Text>
      )}
      {onViewDetails && (
        <Text
          as="span"
          className="radio-option-content-view-details"
          {...styles?.radioOptionViewDetails}
          onClick={handleViewDetails}
        >
          <FormattedMessage
            defaultMessage="View details"
            id="configuration_selector.tile.view_details"
          />
        </Text>
      )}
    </Flex>
  );
}

function RadioOptionExtraPrice({currency, extraPrice, intl, listPrice, styles}) {
  if (!(extraPrice > 0)) return null;

  return (
    <Box className="radio-option-content-extra-price-container" {...styles?.radioOptionExtraPriceContainer}>
      <Text className="radio-option-content-extra-price" {...styles?.radioOptionExtraPrice}>
        <Box as="span" display="inline" whiteSpace="nowrap">
          <Box as="span" display="inline">
            +{' '}
          </Box>
          <Box as="span" display="inline">
            {formatCurrencyHideTrailingZeroCents(intl, extraPrice, currency)}
          </Box>
        </Box>
        {listPrice != null && listPrice > extraPrice && (
          <>
            {' '}
            <Box as="span" display="inline" textDecoration="line-through">
              {formatCurrencyHideTrailingZeroCents(intl, listPrice, currency)}
            </Box>
          </>
        )}
      </Text>
    </Box>
  );
}

function RadioOptionIndicator({hideRadioIndicator, isSelected, styles}) {
  if (hideRadioIndicator) return null;

  return (
    <Box aria-hidden className="radio-option-indicator" {...styles?.radioIndicator}>
      {isSelected && <Box className="radio-option-indicator__dot" {...styles?.radioIndicatorDot} />}
    </Box>
  );
}

function RadioOptionThumbnail({imageUrl, isFlyoutPicker, product, styles}) {
  if (imageUrl) {
    return (
      <Image
        alt={product.name}
        className="radio-option-content-image"
        htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
        htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
        loading="lazy"
        src={imageUrl}
        {...styles?.radioOptionImage}
      />
    );
  }

  if (!isFlyoutPicker) return null;

  return (
    <Box
      aria-hidden
      className="radio-option-content-image radio-option-content-image--placeholder"
      role="presentation"
      {...styles?.radioOptionImagePlaceholder}
    />
  );
}

export function RadioOptionContent({
  extraPrice,
  hideRadioIndicator = false,
  isFlyoutPicker = false,
  isOutOfStock = false,
  listPrice,
  isSelected,
  onViewDetails,
  product,
  containerId,
  styles,
  useCushionSingleTitle = false,
  useListLayout = false,
}) {
  const intl = useIntl();
  const imageUrl = resolveProductThumbnailUrl(product);
  const line1Trimmed = trimNameLine(product.c_nameLine1);
  const line2Trimmed = trimNameLine(product.c_nameLine2);
  const displayPrimary = line1Trimmed || (line2Trimmed ? '' : product.name);
  const cushionSingleTitle = useCushionSingleTitle ? line1Trimmed || product.name || '' : null;
  const showSecondaryNameLine = !useCushionSingleTitle && Boolean(line2Trimmed);
  const currency = product.currency ?? 'USD';
  const containerIdNorm = typeof containerId === 'string' ? containerId.trim().toLowerCase() : '';
  const isCushionContainer = containerIdNorm === 'cushion' || containerIdNorm === 'cushions';
  const shortDescription = isCushionContainer && !isFlyoutPicker ?
    (typeof product.shortDescription === 'string' ?
      product.shortDescription :
      typeof product.c_shortDescription === 'string' ?
        product.c_shortDescription :
        null) :
    null;

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(product);
  };

  const nameBlockProps = {
    containerId,
    cushionSingleTitle,
    displayPrimary,
    handleViewDetails,
    isOutOfStock,
    line2Trimmed,
    onViewDetails,
    product,
    shortDescription,
    showSecondaryNameLine,
    styles,
    useCushionSingleTitle,
    useListLayout: useListLayout || isFlyoutPicker,
  };

  if (isFlyoutPicker || useListLayout) {
    return (
      <Flex alignItems="center" className="radio-option-content-container" width="100%" gap={4}>
        <RadioOptionThumbnail
          imageUrl={imageUrl}
          isFlyoutPicker={isFlyoutPicker}
          product={product}
          styles={styles}
        />
        <RadioOptionNameBlock {...nameBlockProps} />
        <RadioOptionExtraPrice
          currency={currency}
          extraPrice={extraPrice}
          intl={intl}
          listPrice={listPrice}
          styles={styles}
        />
        <RadioOptionIndicator hideRadioIndicator={hideRadioIndicator} isSelected={isSelected} styles={styles} />
      </Flex>
    );
  }

  return (
    <Flex
      alignItems="center"
      className="radio-option-content-container"
      justifyContent="space-between"
      width="100%"
      gap={4}
    >
      <Flex alignItems="center" justifyContent="flex-start" gap={4}>
        <RadioOptionThumbnail
          imageUrl={imageUrl}
          isFlyoutPicker={isFlyoutPicker}
          product={product}
          styles={styles}
        />
        <RadioOptionNameBlock {...nameBlockProps} />
      </Flex>
      <Flex alignItems="center" justifyContent="flex-end" gap={4}>
        <RadioOptionExtraPrice
          currency={currency}
          extraPrice={extraPrice}
          intl={intl}
          listPrice={listPrice}
          styles={styles}
        />
        <RadioOptionIndicator hideRadioIndicator={hideRadioIndicator} isSelected={isSelected} styles={styles} />
      </Flex>
    </Flex>
  );
}

RadioOptionContent.displayName = 'RadioOptionContent';

RadioOptionContent.propTypes = {
  extraPrice: PropTypes.number,
  hideRadioIndicator: PropTypes.bool,
  isFlyoutPicker: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  listPrice: PropTypes.number,
  isSelected: PropTypes.bool,
  onViewDetails: PropTypes.func,
  product: PropTypes.object.isRequired,
  containerId: PropTypes.string,
  styles: PropTypes.object,
  useCushionSingleTitle: PropTypes.bool,
  useListLayout: PropTypes.bool,
};
